import { useGetAgencies } from "@entities/agency";
import { ColumnFilterWrapper } from "@shared/ui/filters/ColumnFilterWrapper";
import { useBriefInfluencerFilterStore } from "@widgets/CampaignBrief/store/useBriefInfluencerFilterStore";
import { Radio, Select, Spin, Typography } from "antd";
import { useState } from "react";

const { Text } = Typography;

const CONTACT_TYPE_OPTIONS = [
  { label: "No Contact", value: "noContact" },
  { label: "All", value: "all" },
  { label: "Influencer", value: "influencer" },
  { label: "Agency", value: "agency" },
  { label: "Specific Agency", value: "specificAgency" },
];

export type ContactFilterValue = {
  primaryContact: {
    type: string[];
    agencyId: string[];
  };
  isCreatorOnboardingPending?: boolean;
};

export interface BriefContactFilterV2Props {
  confirm: () => void;
  close: () => void;
  title?: string;
  value?: ContactFilterValue;
  onApply?: (value: ContactFilterValue) => void;
  filters?: any;
  setFilters?: any;
  before?: React.ReactNode;
}

function normalizePrimaryContact(primaryContact?: {
  type?: string[];
  agencyId?: string[];
}): ContactFilterValue["primaryContact"] | undefined {
  if (!primaryContact) {
    return undefined;
  }

  return {
    type: primaryContact.type ?? [],
    agencyId: primaryContact.agencyId ?? [],
  };
}

function getContactTypeFromFilters(
  primaryContact?: ContactFilterValue["primaryContact"],
  isCreatorOnboardingPending?: boolean
) {
  const contactFilters = primaryContact?.type || [];
  const agencyIds = primaryContact?.agencyId || [];

  if (isCreatorOnboardingPending) {
    return "noContact";
  }

  if (!contactFilters.length && !agencyIds.length) {
    return "all";
  }

  if (
    contactFilters.length === 1 &&
    contactFilters[0] === "Agency" &&
    agencyIds.length > 0 &&
    agencyIds[0]
  ) {
    return "specificAgency";
  }

  if (contactFilters.length === 1 && contactFilters[0] === "Agency") {
    return "agency";
  }

  if (contactFilters.includes("Self") || contactFilters.includes("Manager")) {
    return "influencer";
  }

  return "all";
}

function buildContactFilterValue(
  contactType: string,
  selectedAgency?: string
): ContactFilterValue {
  if (contactType === "noContact") {
    return {
      primaryContact: { type: [], agencyId: [] },
      isCreatorOnboardingPending: true,
    };
  }

  let type: string[] = [];
  let agencyId: string[] = [];

  if (contactType === "agency") {
    type = ["Agency"];
  } else if (contactType === "specificAgency") {
    type = ["Agency"];
    agencyId = selectedAgency ? [selectedAgency] : [];
  } else if (contactType === "influencer") {
    type = ["Manager", "Self"];
  }

  return {
    primaryContact: { type, agencyId },
    isCreatorOnboardingPending: undefined,
  };
}

export default function BriefContactFilterV2({
  confirm,
  close,
  title = "Creator Contact",
  value,
  onApply,
  filters: propsFilters,
  setFilters: propsSetFilters,
  before,
}: BriefContactFilterV2Props) {
  const isControlled = value !== undefined && onApply !== undefined;
  const isPropsFilters = propsFilters !== undefined && propsSetFilters !== undefined;

  const {
    feFilters: storeFilters,
    setFilters: setStoreFilters,
    saveFilters,
  } = useBriefInfluencerFilterStore();

  const activeFilters = isPropsFilters ? propsFilters : storeFilters;

  const activePrimaryContact = normalizePrimaryContact(
    isControlled ? value.primaryContact : activeFilters?.primaryContact
  );
  const activeOnboardingPending = isControlled
    ? value.isCreatorOnboardingPending
    : activeFilters?.statusFilters?.isCreatorOnboardingPending;

  const initialAgencyId = activePrimaryContact?.agencyId[0] || undefined;

  const [contactType, setContactType] = useState(() =>
    getContactTypeFromFilters(activePrimaryContact, activeOnboardingPending)
  );
  const [searchAgency, setSearchAgency] = useState("");
  const [selectedAgency, setSelectedAgency] = useState<string | undefined>(
    initialAgencyId
  );
  const { agencies, isFetching } = useGetAgencies(searchAgency);

  const isFiltered = contactType !== "all";

  const handleApplyFilter = (targetType: string, targetAgency?: string) => {
    const nextValue = buildContactFilterValue(targetType, targetAgency);

    if (isControlled) {
      onApply(nextValue);
    } else if (isPropsFilters) {
      propsSetFilters((prev: any) => {
        if (targetType === "noContact") {
          return {
            ...prev,
            statusFilters: {
              ...prev.statusFilters,
              isCreatorOnboardingPending: true,
            },
            primaryContact: {
              type: [],
              agencyId: [],
            },
          };
        }
        return {
          ...prev,
          primaryContact: nextValue.primaryContact,
          statusFilters: {
            ...prev.statusFilters,
            isCreatorOnboardingPending: undefined,
          },
        };
      });
    } else if (storeFilters) {
      if (targetType === "noContact") {
        setStoreFilters({
          ...storeFilters,
          statusFilters: {
            ...storeFilters.statusFilters,
            isCreatorOnboardingPending: true,
          },
          primaryContact: {
            type: [],
            agencyId: [],
          },
        });
      } else {
        setStoreFilters({
          ...storeFilters,
          primaryContact: nextValue.primaryContact,
          statusFilters: {
            ...storeFilters.statusFilters,
            isCreatorOnboardingPending: undefined,
          },
        });
      }
      saveFilters();
    }
  };

  return (
    <ColumnFilterWrapper
      title={title}
      filterCount={isFiltered ? 1 : 0}
      isFiltered={isFiltered}
      onReset={() => {
        setContactType("all");
        setSelectedAgency(undefined);
        handleApplyFilter("all", undefined);
        confirm();
      }}
      onApply={() => {
        handleApplyFilter(contactType, selectedAgency);
        confirm();
      }}
      onClose={close}
      before={before}
    >
      <div style={{ padding: "8px 4px" }}>
        <div style={{ marginBottom: 12 }}>
          <Text strong style={{ fontSize: 13, color: "#1a1f36" }}>
            Select Type of <span style={{ fontWeight: 600 }}>Contact</span>
          </Text>
        </div>
        <Radio.Group
          value={contactType}
          onChange={(event) => {
            setContactType(event.target.value);
            if (event.target.value !== "specificAgency") {
              setSelectedAgency(undefined);
            }
          }}
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 10,
            marginBottom: 16,
          }}
        >
          {CONTACT_TYPE_OPTIONS.map((opt) => (
            <Radio key={opt.value} value={opt.value}>
              {opt.label}
            </Radio>
          ))}
        </Radio.Group>
        {contactType === "specificAgency" && (
          <div style={{ marginTop: 12 }}>
            <Text strong style={{ fontSize: 13, color: "#1a1f36", display: "block", marginBottom: 6 }}>
              Select an <span style={{ fontWeight: 600 }}>Agency</span>
            </Text>
            <Select
              showSearch
              allowClear
              placeholder="Search Agency"
              value={selectedAgency}
              onChange={setSelectedAgency}
              onSearch={setSearchAgency}
              filterOption={false}
              style={{ width: "100%" }}
              notFoundContent={
                isFetching ? <Spin size="small" /> : "No data available"
              }
            >
              {(agencies?.data || []).map(
                (agency: { _id: string; name: string }) => (
                  <Select.Option key={agency._id} value={agency._id}>
                    {agency.name}
                  </Select.Option>
                )
              )}
            </Select>
          </div>
        )}
      </div>
    </ColumnFilterWrapper>
  );
}
